import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import React, { FC, useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetLoggedInUserDetails } from "../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { MobileNumber } from "../../../../api/services/nisParticipants/models";
import {
  FormController,
  MuiDateField,
  MuiGrid,
  MuiLink,
  MuiMultiSelect,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  RowProps,
  TableHeaderSearchIcon,
} from "../../../../components";
import MuiChip from "../../../../components/chip/MuiChip";
import MuiEllipseLabel from "../../../../components/ellipseLabel/MuiEllipseLabel";
import { StatusBadge } from "../../../../components/statusBadge/StatusBadge";
import { MAX_LENGTH_50, MODULE_PERMISSIONS, PAGE_URLS } from "../../../../helpers/constants";
import {
  DATE_FORMAT,
  EMPTY_VALUE_DASH,
} from "../../../../helpers/constants/common";
import { getDateFormatByCountry } from "../../../../helpers/constants/countryDateFormat";
import { LabelValue, TableRefHandle } from "../../../../helpers/types/common";
import {
  buildActionMenuItems,
  compareDates,
  convertToUserFormat,
  getFullName,
  getValueOrBlank,
  isDateInRange,
  isEmpty,
  isNotEmpty,
} from "../../../../helpers/utils";
import { useMultiSelectARIALabels } from "../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../redux/slices/appState/appStateSelector";
import { ConvertToProgramNavigation } from "../../../viewNISParticipant/components/convertToProgramNavigation/ConvertToProgramNavigation";
import {
  NISParticipantSearchResultProps,
  NISParticipantStatus,
  ProgramNISParticipantsSearchResultFormType,
  ProgramNISParticipantsSearchResultRowValue,
  SearchProgramResultFormKey,
  UniqueProgramSearchNISParticipantsResultFiltersTypes,
} from "../../modelsAndSchemas/models";
import { programSearchSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { NISParticipantSearchResultWrapper } from "./NISParticipantSearchResult.style";

export const NISParticipantAction = {
  ConvertToProgram: "Convert to Program",
  Edit: "Edit",
  View: "View",
} as const;

export const nisParticipantActionMap: Record<string, string[]> = {
  [NISParticipantStatus.Open]: [
    NISParticipantAction.ConvertToProgram,
    NISParticipantAction.Edit,
    NISParticipantAction.View,
  ],
  [NISParticipantStatus.UnableToValidate]: [
    NISParticipantAction.ConvertToProgram,
    NISParticipantAction.Edit,
    NISParticipantAction.View,
  ],
  [NISParticipantStatus.Converted]: [NISParticipantAction.View],
  [NISParticipantStatus.Ineligible]: [
    NISParticipantAction.Edit,
    NISParticipantAction.View,
  ],
  [NISParticipantStatus.CreatedInError]: [
    NISParticipantAction.Edit,
    NISParticipantAction.View,
  ],
};

export const NISParticipantSearchResult: FC<
  NISParticipantSearchResultProps
> = ({ rows, isSearch, pageId, searchType }) => {
  const {
    fetchComponentTitle,
    fetchValueByKeyOfDropdown,
    commonLabels,
    useFetchLabel,
    fetchDropdownValues,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const { hasModulePermission } = usePermissions();

  const tableRef = React.useRef<TableRefHandle>(null);
  const nisParticipantDataRef =
    useRef<ProgramNISParticipantsSearchResultRowValue>();
  const [filteredResults, setFilteredResults] = useState([...rows]);
  const [
    showConvertToProgramConfirmationModal,
    setShowConvertToProgramConfirmationModal,
  ] = useState(false);
  const [uniqueCountries, setUniqueCountries] = useState<LabelValue[]>([]);
  const [uniqueFilters, setUniqueFilters] =
    useState<UniqueProgramSearchNISParticipantsResultFiltersTypes>(() => ({
      participantStatus: [],
      clientManagedStatus: [],
      solutionCategory: [],
      lineOfBusiness: [],
    }));

  // updated sitecore data
  const [allUpdatedRows, setAllUpdatedRows] = useState<
    ProgramNISParticipantsSearchResultRowValue[]
  >([]);

  const navigate = useNavigate();

  const resetPagination = (shouldResetRowsPerPage: boolean = false) => {
    if (tableRef.current) {
      tableRef.current.resetPagination(shouldResetRowsPerPage);
    }
  };

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const { control, setValue, getValues, reset } =
    useForm<ProgramNISParticipantsSearchResultFormType>({
      mode: "all",
      defaultValues: {
        [SearchProgramResultFormKey.participantName]: "",
        [SearchProgramResultFormKey.programName]: "",
        [SearchProgramResultFormKey.nisNumber]: "",
        [SearchProgramResultFormKey.programTemplate]: "",
        [SearchProgramResultFormKey.participantType]: "",
        [SearchProgramResultFormKey.companyName]: "", // clientName
        [SearchProgramResultFormKey.participantStatus]: "",
        [SearchProgramResultFormKey.participantEmail]: "",
        [SearchProgramResultFormKey.clientManagedStatus]: "",
        [SearchProgramResultFormKey.participantMobileNumber]: "",
        [SearchProgramResultFormKey.country]: [],
        [SearchProgramResultFormKey.filterCountryName]: "",
        [SearchProgramResultFormKey.projectManager]: "",
        [SearchProgramResultFormKey.billingContactName]: "",
        [SearchProgramResultFormKey.createdDate]: "",
        [SearchProgramResultFormKey.hrContactName]: "",
        [SearchProgramResultFormKey.clientRelationShipOwner]: "",
        [SearchProgramResultFormKey.lineOfBusiness]: "", // productLine
        [SearchProgramResultFormKey.programStartDate]: "",
        [SearchProgramResultFormKey.solutionCategory]: "",
      },
    });
  const filters = useWatch({ control });

  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);

  const getDropdownOptions = (key: string, optionByValue?: boolean) => {
    return fetchDropdownValues(
      programSearchSiteCoreKeys.resultFormKey,
      key,
      optionByValue,
    );
  };

  const getValueByKeyOfDropdown = (list: string, value: string) => {
    return fetchValueByKeyOfDropdown(
      programSearchSiteCoreKeys.resultFormKey,
      list,
      value,
    );
  };

  useEffect(() => {
    resetPagination(true);
    const myData = [...rows];

    const uniqueCountries = Array.from(
      new Set(
        myData
          .map((item) => item?.country?.name ?? "")
          .filter((country) => country !== ""),
      ),
    ).map((country) => ({ value: country, label: country }));

    setUniqueCountries(uniqueCountries);
    reset();
  }, [rows]);

  const getUniqueFilters = (
    data: ProgramNISParticipantsSearchResultRowValue[],
    key: string,
  ): LabelValue[] => {
    return Array.from(
      new Set(
        data
          .filter((participantItem) => participantItem[key]) // Filter out null or undefined values
          .map((participantItem) => participantItem[key]) // Extract values for the given key
          .reduce((acc, val) => acc.concat(val), []), // Flatten the array if values are arrays
      ),
    ).map((value) => ({ label: value as string, value: value as string })); // Format as value-label pairs
  };

  const getParticipantMobileNumber = (item?: MobileNumber) => {
    return isNotEmpty(item) && isNotEmpty(item?.number)
      ? `${item?.countryCode} ${item?.number}`
      : EMPTY_VALUE_DASH;
  };

  useEffect(() => {
    if (!siteCoreLoading) {
      const localData = [...rows];
      localData.forEach((data) => {
        data.participantName = getFullName(
          data.participantFirstName,
          data.participantLastName,
        );
        data.nisNumberName = data.nisNumber?.name;
        data.programTemplateName = data?.programTemplate?.name;
        data.participantTypeKey = data.participantType;
        data.participantType = getValueByKeyOfDropdown(
          programSearchSiteCoreKeys.candidateTypeList,
          data.participantType,
        );
        data.statusKey = data.participantStatus;
        data.participantStatus = getValueByKeyOfDropdown(
          programSearchSiteCoreKeys.nisParticipantStatusList,
          data.participantStatus,
        );
        data.companyName = data.client?.name;
        data.participantMobileNumberDisplay = getParticipantMobileNumber(
          data?.participantMobileNumber,
        );
        data.clientManagedStatus = data.clientManagedStatus
          ? getValueByKeyOfDropdown(
              programSearchSiteCoreKeys.clientManagedStatusListResult,
              data.clientManagedStatus,
            )
          : "";
        data.projectManagerName = getFullName(
          data?.projectManager?.firstName,
          data?.projectManager?.lastName,
        );
        data.countryName = data?.country?.name;
        data.billingContactName = getFullName(
          data?.programBillingContact?.firstName,
          data?.programBillingContact?.lastName,
        );
        data.hrContactName = getFullName(
          data?.hrContact?.firstName,
          data?.hrContact?.lastName,
        );
        data.lineOfBusiness = data?.productLine?.name;
        data.clientRelationShipOwner = getFullName(
          data?.localClientRelationshipOwner?.firstName,
          data?.localClientRelationshipOwner?.lastName,
        );
      });
      setFilteredResults([...localData]);
      setAllUpdatedRows(localData);

      setUniqueFilters((prevFilters) => ({
        ...prevFilters,
        [SearchProgramResultFormKey.lineOfBusiness]: getUniqueFilters(
          localData,
          SearchProgramResultFormKey.lineOfBusiness,
        ),
        [SearchProgramResultFormKey.solutionCategory]: getUniqueFilters(
          localData,
          SearchProgramResultFormKey.solutionCategory,
        ),
        [SearchProgramResultFormKey.participantStatus]: getDropdownOptions(
          programSearchSiteCoreKeys.nisParticipantStatusList,
          true,
        ),
        [SearchProgramResultFormKey.clientManagedStatus]: getDropdownOptions(
          programSearchSiteCoreKeys.clientManagedStatusListResult,
          true,
        ),
        [SearchProgramResultFormKey.participantType]: getDropdownOptions(
          programSearchSiteCoreKeys.candidateTypeList,
          true,
        ),
      }));
    }
  }, [rows, siteCoreLoading]);

  const checkFilterMatch = (inputValue: string, rowItem: string) => {
    return isEmpty(inputValue?.toLowerCase())
      ? true
      : isNotEmpty(rowItem) &&
          rowItem?.toLowerCase().includes(inputValue?.toLowerCase());
  };

  useEffect(() => {
    if (isNotEmpty(allUpdatedRows)) {
      const {
        participantName,
        programName,
        programTemplate,
        nisNumber,
        participantType,
        participantStatus,
        companyName,
        clientManagedStatus,
        participantEmail,
        participantMobileNumber,
        projectManager,
        filterCountryName,
        createdDate,
        billingContactName,
        hrContactName,
        lineOfBusiness,
        clientRelationShipOwner,
        solutionCategory,
        programStartDate,
        projectKey,
        projectName,
      } = getValues();

      const myData = allUpdatedRows.filter((item) => {
        const programNameMatch = checkFilterMatch(
          getValueOrBlank(programName),
          getValueOrBlank(item.programName),
        );

        const programTemplateMatch = checkFilterMatch(
          getValueOrBlank(programTemplate),
          getValueOrBlank(item.programTemplateName),
        );

        const nisNumberMatch = checkFilterMatch(
          nisNumber,
          item.nisNumber?.name,
        );

        const projectKeyMatch = checkFilterMatch(
          getValueOrBlank(projectKey),
          getValueOrBlank(item.projectKey),
        );

        const projectNameMatch = checkFilterMatch(
          getValueOrBlank(projectName),
          getValueOrBlank(item.projectName),
        );

        const companyNameMatch = checkFilterMatch(
          companyName,
          item.client?.name,
        );

        const participantNameMatch = checkFilterMatch(
          getValueOrBlank(participantName),
          getFullName(item.participantFirstName, item.participantLastName),
        );

        const participantMobileNumberMatch = checkFilterMatch(
          getValueOrBlank(participantMobileNumber),
          getParticipantMobileNumber(item?.participantMobileNumber),
        );

        const participantEmailMatch = checkFilterMatch(
          getValueOrBlank(participantEmail),
          getValueOrBlank(item.participantEmail),
        );

        const projectManagerMatch = checkFilterMatch(
          getValueOrBlank(projectManager),
          getFullName(
            item?.projectManager?.firstName,
            item?.projectManager?.lastName,
          ),
        );

        const billingContactNameMatch = checkFilterMatch(
          getValueOrBlank(billingContactName),
          getFullName(
            item?.programBillingContact?.firstName,
            item?.programBillingContact?.lastName,
          ),
        );

        const hrContactNameMatch = checkFilterMatch(
          getValueOrBlank(hrContactName),
          getFullName(item?.hrContact?.firstName, item?.hrContact?.lastName),
        );

        const lineOfBusinessMatch = checkFilterMatch(
          getValueOrBlank(lineOfBusiness),
          getValueOrBlank(item.productLine?.name),
        );

        const clientRelationShipOwnerMatch = checkFilterMatch(
          getValueOrBlank(clientRelationShipOwner),
          getFullName(
            item?.localClientRelationshipOwner?.firstName,
            item?.localClientRelationshipOwner?.lastName,
          ),
        );

        const participantTypeMatch = checkFilterMatch(
          getValueOrBlank(participantType),
          getValueOrBlank(item.participantType),
        );

        const solutionCategoryMatch = checkFilterMatch(
          getValueOrBlank(solutionCategory),
          getValueOrBlank(item.solutionCategory),
        );

        const statusMatch =
          isEmpty(participantStatus) ||
          item.participantStatus?.toLowerCase() ===
            participantStatus?.toLowerCase();

        const clientManagedStatusMatch =
          isEmpty(clientManagedStatus) ||
          item.clientManagedStatus?.toLowerCase() ===
            clientManagedStatus?.toLowerCase();

        const countryMatch =
          !filterCountryName?.length ||
          filterCountryName.some((c: string) =>
            item?.country?.name?.toLowerCase().includes(c.toLowerCase()),
          );

        let isDateMatch = compareDates(
          getValueOrBlank(createdDate),
          getValueOrBlank(item?.createdDate),
          getValueOrBlank(userCountryDateFormat),
        );

        let isProgramStartDateMatch = true;

        if (programStartDate) {
          if (!item?.programStartDate) {
            isProgramStartDateMatch = false;
          } else {
            const selectedDate = dayjs(programStartDate, userCountryDateFormat);
            const startOfSelectedDateMonth = selectedDate.startOf("month");
            const endOfSelectedDateMonth = selectedDate.endOf("month");

            const itemDate = dayjs(item?.programStartDate);

            isProgramStartDateMatch = isDateInRange(
              itemDate,
              startOfSelectedDateMonth,
              endOfSelectedDateMonth,
            );
          }
        }

        return (
          programNameMatch &&
          programTemplateMatch &&
          nisNumberMatch &&
          companyNameMatch &&
          statusMatch &&
          isDateMatch &&
          isProgramStartDateMatch &&
          countryMatch &&
          participantNameMatch &&
          participantMobileNumberMatch &&
          participantEmailMatch &&
          projectManagerMatch &&
          billingContactNameMatch &&
          hrContactNameMatch &&
          lineOfBusinessMatch &&
          clientRelationShipOwnerMatch &&
          participantTypeMatch &&
          clientManagedStatusMatch &&
          solutionCategoryMatch &&
          projectKeyMatch &&
          projectNameMatch
        );
      });
      setFilteredResults([...myData]);
      resetPagination();
    }
  }, [filters]);

  const getLabel = useFetchLabel(programSearchSiteCoreKeys.resultFormKey);
  const getSearchFormLabel = useFetchLabel(
    programSearchSiteCoreKeys.searchFormKey,
  );

  const navigateToViewNISPage = (
    row: ProgramNISParticipantsSearchResultRowValue,
  ) => {
    navigate(`${PAGE_URLS.VIEW_NIS_PARTICIPANT}/${row.nisNumber?.id}`, {
      state: { from: PAGE_URLS.SEARCH_PROGRAM },
    });
  };

  const navigateToEditNISPage = (
    row: ProgramNISParticipantsSearchResultRowValue,
  ) => {
    navigate(`${PAGE_URLS.EDIT_NIS_PARTICIPANT}/${row.nisNumber?.id}`, {
      state: { from: PAGE_URLS.SEARCH_PROGRAM },
    });
  };

  const isValidClient = (
    data: ProgramNISParticipantsSearchResultRowValue,
  ): boolean => {
    return data?.client && isNotEmpty(data?.client?.id);
  };
  
  const columns = [
    {
      id: SearchProgramResultFormKey.participantName,
      label: getLabel(programSearchSiteCoreKeys.participantNameLabel),
      isEmptyValue: true,
      isSortVisible: true,
      className: "cell-program-Key-number",
      render: (row: RowProps) =>
        !row.participantFirstName && !row.participantLastName ? null : (
          <MuiLink
            href={`${PAGE_URLS.VIEW_NIS_PARTICIPANT}/${row.nisNumber?.id}`}
            isOpenNewTab
            id={`${pageId}-view-nis-participant`}
            data-testid="view-nis-participant-link"
          >
            <MuiEllipseLabel
              labelTypoClassName="Link"
              text={row.participantName}
              labelTypoVariant="h3"
              placement="bottom-start"
              pointerCursor
            />
          </MuiLink>
        ),
    },
    {
      id: SearchProgramResultFormKey.programName,
      label: getLabel(programSearchSiteCoreKeys.programNameLabel),
      className: "cell-program-name",
      isSortVisible: true,
    },
    {
      id: SearchProgramResultFormKey.nisNumberName,
      label: getLabel(programSearchSiteCoreKeys.keyNISNumber),
      isSortVisible: true,
      className: "cell-program-Key-number",
    },
    {
      id: SearchProgramResultFormKey.programTemplateName,
      label: getLabel(programSearchSiteCoreKeys.programTemplateTableColumn),
      isSortVisible: true,
      className: "cell-program-Key-number",
    },
    {
      id: SearchProgramResultFormKey.participantType,
      label: getLabel(programSearchSiteCoreKeys.participantTypeLabel),
      isSortVisible: true,
      className: "cell-program-Key-number",
    },
    {
      id: SearchProgramResultFormKey.participantStatus,
      label: getLabel(programSearchSiteCoreKeys.participantStatusTableColumn),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-status",
      render: (row: RowProps) => (
        <StatusBadge status={row.statusKey} label={row.participantStatus} />
      ),
    },
    {
      id: SearchProgramResultFormKey.companyName,
      label: getLabel(programSearchSiteCoreKeys.clientEmployerTableColumn),
      isSortVisible: true,
      className: "cell-company-name",
    },
    {
      id: SearchProgramResultFormKey.clientManagedStatus,
      label: getLabel(programSearchSiteCoreKeys.clientManagedStatusLabel),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-status",
      render: (row: RowProps) =>
        row.clientManagedStatus ? (
          <MuiChip
            data={[
              {
                label: row.clientManagedStatus,
                value: row.clientManagedStatus,
              },
            ]}
            isEditable={false}
            showMoreARIALabel={commonARIALabel.showNMore}
            removeChipNameARIALabel={commonARIALabel.removeChipName}
          />
        ) : (
          <MuiTypography variant="h3">{EMPTY_VALUE_DASH}</MuiTypography>
        ),
    },
    {
      id: SearchProgramResultFormKey.participantEmail,
      label: getLabel(programSearchSiteCoreKeys.participantEmailLabel),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-Key-number",
    },
    {
      id: SearchProgramResultFormKey.participantMobileNumberDisplay,
      label: getLabel(programSearchSiteCoreKeys.participantMobileNumberLabel),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-Key-number",
    },
    {
      id: SearchProgramResultFormKey.projectManagerName,
      label: getLabel(programSearchSiteCoreKeys.projectManagerTableColumn),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-Key-number",
    },
    {
      id: SearchProgramResultFormKey.countryName,
      label: getLabel(programSearchSiteCoreKeys.countryLabel),
      isSortVisible: true,
      className: "cell-country",
    },
    {
      id: SearchProgramResultFormKey.createdDate,
      label: commonLabels.createdDate,
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiTypography variant="h3">
          {isNotEmpty(row.createdDate)
            ? convertToUserFormat(row.createdDate, userCountryDateFormat)
            : EMPTY_VALUE_DASH}
        </MuiTypography>
      ),
    },
    {
      id: SearchProgramResultFormKey.billingContactName,
      label: getLabel(programSearchSiteCoreKeys.billingContact),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-Key-number",
    },
    {
      id: SearchProgramResultFormKey.hrContactName,
      label: getLabel(programSearchSiteCoreKeys.hrContactTableColumn),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-Key-number",
    },
    {
      id: SearchProgramResultFormKey.lineOfBusiness,
      label: getLabel(programSearchSiteCoreKeys.productLineTableColumn),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-Key-number",
    },
    {
      id: SearchProgramResultFormKey.clientRelationShipOwner,
      label: getLabel(
        programSearchSiteCoreKeys.localClientRelationshipOwnerLabel,
      ),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-Key-number",
    },
    {
      id: SearchProgramResultFormKey.solutionCategory,
      label: getLabel(programSearchSiteCoreKeys.solutionCategoryTableColumn),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-Key-number",
    },
    {
      id: SearchProgramResultFormKey.programStartDate,
      label: getLabel(
        programSearchSiteCoreKeys.programStartMonthOrYearTableColumn,
      ),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiTypography variant="h3">
          {isNotEmpty(row.programStartDate)
            ? convertToUserFormat(row.programStartDate, DATE_FORMAT.MMMM_YYYY)
            : EMPTY_VALUE_DASH}
        </MuiTypography>
      ),
    },
    {
      id: "action",
      label: getLabel(programSearchSiteCoreKeys.actionLabel),
      render: (row: RowProps) => {
        const allowedActions = nisParticipantActionMap[row.statusKey] ?? [];
        const isClientValid = isValidClient(
          row as ProgramNISParticipantsSearchResultRowValue,
        );
        const menuItems = buildActionMenuItems(
          row,
          [
            {
              show: () =>
                allowedActions.includes(
                  NISParticipantAction.ConvertToProgram,
                ) && isClientValid,
              // permissionKey: MODULE_PERMISSIONS.PROGRAM_EDIT,
              create: (row) => ({
                onClick: () => {
                  // Navigate to Convert to Program page with selected participant's details
                  nisParticipantDataRef.current =
                    row as ProgramNISParticipantsSearchResultRowValue;
                  setShowConvertToProgramConfirmationModal(true);
                },
                dataTestId: "convert-to-program",
                label: getLabel(
                  programSearchSiteCoreKeys.convertToProgramAction,
                ),
                ariaLabel: getLabel(
                  programSearchSiteCoreKeys.convertToProgramAction,
                ),
              }),
            },
            {
              show: () => allowedActions.includes(NISParticipantAction.Edit),
              permissionKey: MODULE_PERMISSIONS.NIS_EDIT,
              create: (row) => ({
                onClick: () => {
                  // Navigate to Edit NIS Participant page with selected participant's details
                  navigateToEditNISPage(
                    row as ProgramNISParticipantsSearchResultRowValue,
                  );
                },
                dataTestId: "edit",
                label: commonLabels.edit,
                ariaLabel: commonLabels.edit,
              }),
            },
            {
              show: () => allowedActions.includes(NISParticipantAction.View),
              permissionKey: MODULE_PERMISSIONS.NIS_VIEW,
              create: (row) => ({
                onClick: () => {
                  // Navigate to View NIS Participant page with selected participant's details
                  navigateToViewNISPage(
                    row as ProgramNISParticipantsSearchResultRowValue,
                  );
                },
                dataTestId: "view",
                label: commonLabels.view,
                ariaLabel: commonLabels.view,
              }),
            },
          ],
          hasModulePermission,
        );

        return menuItems ? <MuiOptionMenu menuItems={menuItems} /> : null;
      },
    },
  ];

  const handleMuiDateChange = (newValue: dayjs.Dayjs | null) => {
    let dateValue = "";
    if (newValue?.isValid()) {
      dateValue = newValue.format(userCountryDateFormat);
    }
    setValue(SearchProgramResultFormKey.programStartDate, dateValue);
  };

  const renderFilterTextField = (name: string) => {
    return (
      <FormController key={name} control={control} name={name}>
        <MuiTextField
          key={name}
          id={`${pageId}-${name}`}
          aria-label={commonLabels.search}
          startAdornment={<TableHeaderSearchIcon />}
          data-testid="input-field"
          showCloseIcon={true}
          placeholder={commonLabels.search}
          maxLength={MAX_LENGTH_50}
          requiredARIALabel={commonARIALabel.required}
          shouldTrimSpaces={false}
        />
      </FormController>
    );
  };

  const renderFilterDropdown = (name: string) => {
    const options =
      uniqueFilters[
        name as unknown as keyof UniqueProgramSearchNISParticipantsResultFiltersTypes
      ];
    return (
      <FormController key={name} control={control} name={name}>
        <MuiSelect
          id={`${pageId}-${name}`}
          value=""
          label=""
          options={options}
          visibleDefaultSelectOption={true}
          requiredARIALabel={commonARIALabel.required}
          placeholder={commonLabels.selectPlaceHolder}
        />
      </FormController>
    );
  };

  const tableHeaderForm = [
    renderFilterTextField(SearchProgramResultFormKey.participantName),
    renderFilterTextField(SearchProgramResultFormKey.programName),
    renderFilterTextField(SearchProgramResultFormKey.nisNumber),
    renderFilterTextField(SearchProgramResultFormKey.programTemplate),
    renderFilterDropdown(SearchProgramResultFormKey.participantType),
    renderFilterDropdown(SearchProgramResultFormKey.participantStatus),
    renderFilterTextField(SearchProgramResultFormKey.companyName),
    renderFilterDropdown(SearchProgramResultFormKey.clientManagedStatus),
    renderFilterTextField(SearchProgramResultFormKey.participantEmail),
    renderFilterTextField(SearchProgramResultFormKey.participantMobileNumber),
    renderFilterTextField(SearchProgramResultFormKey.projectManager),
    <FormController
      key={SearchProgramResultFormKey.filterCountryName}
      name={SearchProgramResultFormKey.filterCountryName}
      control={control}
    >
      <MuiMultiSelect
        id={`${pageId}-${SearchProgramResultFormKey.country}`}
        options={uniqueCountries}
        label=""
        showSearch={true}
        disabled={false}
        textFieldWidth={0}
        showChip={false}
        selectPlaceHolder={commonLabels.selectPlaceHolder}
        selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
        searchPlaceHolder={commonLabels.search}
        doneText={commonLabels.done}
        allARIAProps={multiSelectARIALabels}
      />
    </FormController>,
    <FormController
      key={SearchProgramResultFormKey.createdDate}
      name={SearchProgramResultFormKey.createdDate}
      control={control}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          name={SearchProgramResultFormKey.createdDate}
          format={userCountryDateFormat}
          valueFormat={userCountryDateFormat}
          clearable
          value={
            getValues(SearchProgramResultFormKey.createdDate) !== ""
              ? dayjs(
                  getValues(SearchProgramResultFormKey.createdDate),
                  userCountryDateFormat,
                )
              : null
          }
          onChange={(newValue) => {
            setValue(
              SearchProgramResultFormKey.createdDate,
              newValue?.format(userCountryDateFormat) ?? "",
            );
          }}
          selectDateARIALabel={commonARIALabel.selectDate}
          requiredARIALabel={commonARIALabel.required}
        />
      </LocalizationProvider>
    </FormController>,
    renderFilterTextField(SearchProgramResultFormKey.billingContactName),
    renderFilterTextField(SearchProgramResultFormKey.hrContactName),
    renderFilterDropdown(SearchProgramResultFormKey.lineOfBusiness),
    renderFilterTextField(SearchProgramResultFormKey.clientRelationShipOwner),
    renderFilterDropdown(SearchProgramResultFormKey.solutionCategory),
    <FormController
      key={SearchProgramResultFormKey.programStartDate}
      name={SearchProgramResultFormKey.programStartDate}
      control={control}
    >
      <LocalizationProvider
        localeText={{
          fieldMonthPlaceholder: () =>
            getSearchFormLabel(programSearchSiteCoreKeys.monthPlaceholder),
          fieldYearPlaceholder: () =>
            getSearchFormLabel(programSearchSiteCoreKeys.yearPlaceholder),
        }}
        dateAdapter={AdapterDayjs}
      >
        <MuiDateField
          clearable
          value={
            getValues().programStartDate !== ""
              ? dayjs(getValues().programStartDate, userCountryDateFormat)
              : null
          }
          views={["month", "year"]}
          format={DATE_FORMAT.MMM_YYYY_SLASH}
          onChange={handleMuiDateChange}
          selectDateARIALabel={commonARIALabel.selectDate}
          requiredARIALabel={commonARIALabel.required}
        />
      </LocalizationProvider>
    </FormController>,
    <></>,
  ];

  return (
    <NISParticipantSearchResultWrapper>
      <MuiGrid container>
        <MuiGrid className="form-header" size={{ xs: 12 }}>
          <MuiTypography className="form-header-left" variant="h2SemiBold">
            {fetchComponentTitle(programSearchSiteCoreKeys.resultFormKey)}
          </MuiTypography>
        </MuiGrid>
        <MuiGrid container>
          <MuiGrid className="header-right" size={{ xs: 12 }}>
            <MuiTable
              pageId={pageId}
              ref={tableRef}
              columns={columns}
              tableHeaderForm={!siteCoreLoading ? tableHeaderForm : []}
              tableResults={[...filteredResults]}
              emptyMessage={commonLabels.applyNecessaryFiltersMessage}
              noDataInApi={rows.length === 0 && !isSearch}
              priorityResults={[]}
              noResultMessage={commonLabels.noResult}
              isShowDashIfValueEmpty
              isLastColumnSticky
            />
          </MuiGrid>
        </MuiGrid>
      </MuiGrid>
      {showConvertToProgramConfirmationModal &&
        nisParticipantDataRef?.current && (
          <ConvertToProgramNavigation
            nisParticipantData={nisParticipantDataRef.current}
            parentKey={programSearchSiteCoreKeys.resultFormKey}
            confirmationKey={
              programSearchSiteCoreKeys.convertToProgramConfirmationMessage
            }
            showConvertToProgramConfirmationModal={
              showConvertToProgramConfirmationModal
            }
            setShowConvertToProgramConfirmationModal={
              setShowConvertToProgramConfirmationModal
            }
          />
        )}
    </NISParticipantSearchResultWrapper>
  );
};
